import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import { SeparatorType } from './separator-rect';
import { TextLeafNode } from './text-leaf-node';
import { VisualBlock, VisualBlockFeatureType } from './visual-block';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';

export class ContentTree {
    public children: Array<ContentTreeNode> = [];
    private slideModel: ISlideModel;

    public constructor(slideModel: ISlideModel) {
        this.slideModel = slideModel;
    }

    /**
     * 第一个有排布信息的节点
     */
    public getFirstHOrHNode(): ContentTreeNode | undefined {
        if (this.children.length < 1) return undefined;
        // slide根结点目前只有一个
        let current: ContentTreeNode = this.children[0];
        const first = this.getFirstContainTextNode();
        if (first) return first;
        while (current.type === ContentTreeNodeType.None) {
            if (current.children.length > 0) {
                current = current.children[0];
            } else {
                break;
            }
        }
        if (current.type !== ContentTreeNodeType.None) {
            return current.children[0];
        }
        return undefined;
    }

    public getFirstContainTextNode(): ContentTreeNode | undefined {
        if (this.children.length < 1) return undefined;
        return this.children[0].getFirstContainTextNode();
    }

    public getRectsOfNodes(): IRect[] {
        const rects: IRect[] = [];
        this.children.forEach((child) => {
            rects.push(...this.getRectsFromContentNode(child));
        });
        return rects;
    }

    public getTextTree(): any[] {
        return [this.children[0].getTextTree()];
    }

    // 删除掉数组以及数组的子元素中所有的空数组
    private removeEmptyArraysAndUndefined(arr: any[]): any[] {
        return arr
            .filter(item => !(Array.isArray(item) && item.length === 0) && item !== undefined)
            .map(item => (Array.isArray(item) ? this.removeEmptyArraysAndUndefined(item) : item));
    }

    // 将数组以及数组的子元素中所有的数组长度为1的数组往上层提
    private flattenSingleElementArrays(arr: any[]): any[] {
        const flatArray = arr.reduce((accumulator: any[], item: any) => {
            if (Array.isArray(item)) {
                if (item.length === 1) {
                    accumulator.push(...this.flattenSingleElementArrays(item));
                } else {
                    accumulator.push(this.flattenSingleElementArrays(item));
                }
            } else {
                accumulator.push(item);
            }
            return accumulator;
        }, []);

        return flatArray;
    }

    private getRectsFromContentNode(contentNode: ContentTreeNode): IRect[] {
        const rects: IRect[] = [];
        rects.push(contentNode.getRect());
        contentNode.children.forEach((child) => {
            rects.push(...this.getRectsFromContentNode(child));
        });
        return rects;
    }
}

export enum ContentTreeNodeType {
    None, // 没有元素，或者单个元素做的背景，或者有嵌套结构的元素和其他元素(这种情况会有两个子Node)
    Horizontal, // 子节点竖直排列的，可以理解为一堆横条从上到下
    Vertical, // 子节点水平排列的，可以理解为一堆横条从左到右
}

export enum NodeChildrenStructType {
    Nested, // 嵌套结构，默认为嵌套结构，相当于层次结构
    Parallel, // 并列结构
}

/**
 * 语义块
 */
export class ContentTreeNode {
    // 得到语义块数组中语义块之间的关系，嵌套或者并列
    public static getContentNodesStructType(
        nodes: Array<ContentTreeNode>,
        separatorType: SeparatorType,
    ): NodeChildrenStructType {
        // 水平分隔条产生的语义节点数组是一堆横向的矩形，按照竖直方向从上到下排序
        const name1 = separatorType === SeparatorType.Horizontal ? 'top' : 'left';
        const name2 = separatorType === SeparatorType.Horizontal ? 'height' : 'width';
        // 按分隔调相反方向排序后的语义块
        const sortedNodes = nodes.sort((a, b) => a.getRect()[name1] - b.getRect()[name1]);
        if (sortedNodes.length > 1) {
            if (sortedNodes[0].isSimilarStructure(sortedNodes[1], [VisualBlockFeatureType.Frame])) {
                return NodeChildrenStructType.Parallel;
            }
        }
        return NodeChildrenStructType.Nested;
    }
    /**
     * 当前的语义块包含的页面块
     */
    public visualBlocks: Array<VisualBlock> = [];
    /**
     * 子语义块。如果type为none则只有一个children
     */
    public children: Array<ContentTreeNode> = [];

    public parent?: ContentTreeNode;

    /**
     * 当前节点下面的子节点的排列方式
     */
    public type: ContentTreeNodeType;

    /**
     * 子元素是个什么结构，
     */
    public childrenStructType: NodeChildrenStructType;

    private _rect?: IRect;

    private _currentNodeBlocks?: Array<VisualBlock>;

    public constructor(rect?: IRect, type?: ContentTreeNodeType) {
        this._rect = rect;
        this.type = type || ContentTreeNodeType.None;
        this.childrenStructType = NodeChildrenStructType.Nested;
    }

    public getCurrentNodeBlocks(): Array<VisualBlock> {
        if (!this._currentNodeBlocks) {
            this._currentNodeBlocks = this.removeDuplicates(
                this.visualBlocks,
                this.children.map(child => child.visualBlocks),
            );
        }
        return this._currentNodeBlocks;
    }

    // eslint-disable-next-line complexity
    public getFirstContainTextNode(): ContentTreeNode | undefined {
        // slide根结点目前只有一个
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let current: ContentTreeNode = this;
        while (current.children.length >= 0) {
            if (current.children.length === 1) {
                current = current.children[0];
            } else {
                // 并列的没有必要给出去了
                if (current.childrenStructType === NodeChildrenStructType.Parallel) return undefined;
                let hasTextNum = 0;
                let firstHasTextNode;
                for (let i = 0; i < current.children.length; i++) {
                    if (current.children[i].hasText()) {
                        hasTextNum += 1;
                        if (!firstHasTextNode) {
                            firstHasTextNode = current.children[i];
                        }
                    }
                }
                if (hasTextNum === 0) return undefined;
                if (hasTextNum > 1 && firstHasTextNode) {
                    return firstHasTextNode;
                }
                // 只有一个节点有文字，继续往下层找
                if (firstHasTextNode) {
                    current = firstHasTextNode;
                } else {
                    break;
                }
            }
        }
        if (current.hasText()) return current;
        return undefined;
    }

    /**
     * 获取当前节点的深度
     */
    public getDepth(): number {
        let depth = 0;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let currentNode: ContentTreeNode = this;
        while (currentNode.parent) {
            currentNode = currentNode.parent;
            depth += 1;
        }
        return depth;
    }

    // 页面块的包裹矩形区域
    public getRect(): IRect {
        if (this._rect) return this._rect;
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        this.visualBlocks.forEach((block) => {
            const paraRect = block.rect;
            minX = Math.min(minX, paraRect.left);
            minY = Math.min(minY, paraRect.top);
            maxX = Math.max(maxX, paraRect.left + paraRect.width);
            maxY = Math.max(maxY, paraRect.top + paraRect.height);
        });
        this._rect = {
            left: minX,
            top: minY,
            width: maxX - minX,
            height: maxY - minY,
        };
        return this._rect;
    }

    // 子页面块如果有上下或者左右关系后的分割矩形区域
    public getChildAlignRects(): IRect[] {
        if (this.type === ContentTreeNodeType.None) {
            return [this.getRect()];
        }
        const selfRect = this.getRect();
        const rects: IRect[] = [];
        this.children.forEach((child) => {
            const childRect = child.getRect();
            const childAlignRect = {
                left: childRect.left,
                top: childRect.top,
                width: childRect.width,
                height: childRect.height,
            };
            if (this.type === ContentTreeNodeType.Vertical) {
                childAlignRect.left = selfRect.left;
                childAlignRect.width = selfRect.width;
            } else {
                childAlignRect.top = selfRect.top;
                childAlignRect.height = selfRect.height;
            }
            rects.push(childAlignRect);
        });
        return rects;
    }

    /**
     * 和另一个语义块的相似度
     * @param node
     */
    public similarityToNode(node: ContentTreeNode): number {
        return 10;
    }

    /**
     * 判断和另一个语义块是否是一样的结构
     * @param node
     */
    // eslint-disable-next-line complexity
    public isSameStructure(node: ContentTreeNode): boolean {
        if (this.type !== node.type) {
            return false;
        }
        const blocks1 = this.visualBlocks.filter(block => !block.isLine());
        const blocks2 = node.visualBlocks.filter(block => !block.isLine());
        if (blocks1.length !== blocks2.length) return false;
        for (let i = 0; i < blocks1.length; i++) {
            if (!blocks1[i].isSameToOther(blocks2[i])) return false;
        }
        return true;
    }

    /**
     * 比较两个内容块是不是比较相似
     * @param node
     * @param exceptFeatureTypes 除了这些feature之外其他属性几乎相同
     */
    public isSimilarStructure(node: ContentTreeNode, exceptFeatureTypes?: Array<VisualBlockFeatureType>): boolean {
        this.visualBlocks.sort((a, b) => Number(a.listIndex) - Number(b.listIndex));
        node.visualBlocks.sort((a, b) => Number(a.listIndex) - Number(b.listIndex));
        // 数量相差太多，返回false
        if (Math.abs((this.visualBlocks.length / node.visualBlocks.length) - 1) > 0.2) return false;
        let similarNum = 0;
        for (let i = 0; i < this.visualBlocks.length; i++) {
            let findSimilar = false;
            for (let j = 0; j < node.visualBlocks.length; j++) {
                if (this.visualBlocks[i].isSimilarToOther(node.visualBlocks[j], exceptFeatureTypes)) {
                    findSimilar = true;
                    similarNum += 1;
                    break;
                }
            }
        }
        if (similarNum > 0) {
            return true;
        }
        return false;
    }

    public hasText(): boolean {
        for (let i = 0; i < this.visualBlocks.length; i++) {
            if (this.visualBlocks[i].hasText()) return true;
        }
        return false;
    }

    public getTextFontSize(): number {
        for (let i = 0; i < this.visualBlocks.length; i++) {
            const fontSize = this.visualBlocks[i].getFirstFontSize();
            if (fontSize) return fontSize;
        }
        return 0;
    }

    // eslint-disable-next-line complexity
    public getTextTree(): Array<any> | undefined | TextLeafNode {
        if (this.children.length === 0) {
            if (this.visualBlocks.length === 0) return undefined;
            if (this.visualBlocks.length === 1) {
                const text = this.visualBlocks[0].getText();
                if (text) return new TextLeafNode(text, this);
                return undefined;
            }
            if (this.visualBlocks.length > 1) {
                // 复杂块，里面不再拆分了，内容一并提供
                let text = '';
                for (let i = 0; i < this.visualBlocks.length; i++) {
                    const curText = this.visualBlocks[i].getText();
                    if (curText) {
                        if (text.length > 0) {
                            text += `\n${curText}`;
                        } else {
                            text = curText;
                        }
                    }
                }
                return new TextLeafNode(text, this);
            }
        }
        let childrenTextTrees: Array<any> = [];
        this.children.forEach((node) => {
            const nodeTextTree = node.getTextTree();
            if (nodeTextTree) {
                childrenTextTrees.push(nodeTextTree);
            }
        });
        childrenTextTrees = childrenTextTrees.filter(item => item
            && ((Array.isArray(item) && item.length > 0) || item instanceof TextLeafNode));
        const childrenLength = childrenTextTrees.length;
        if (childrenLength === 0) return undefined;
        if (childrenLength === 1) return this.getSimpleTextTree(childrenTextTrees[0]);
        if (childrenLength > 1) {
            if (this.childrenStructType === NodeChildrenStructType.Nested) {
                // 嵌套结构
                if (childrenLength > 1) {
                    let result: Array<any> = [childrenTextTrees[childrenLength - 1]];
                    for (let i = childrenLength - 2; i >= 0; i--) {
                        const current = childrenTextTrees[i];
                        if (current instanceof TextLeafNode) {
                            result = [current, result];
                        } else if (Array.isArray(current) && current.length > 0) {
                            if (current.length === 1) {
                                // 单个的话理论上只有最里面的字符串
                                result = [current[0], result];
                            } else if (current.length === 2 && Array.isArray(current[1])) {
                                // 嵌套结构
                                const lastNestedTextTree = this.getLastNestedTextTree(current[1]); // 嵌套最深层
                                if (Array.isArray(lastNestedTextTree) && lastNestedTextTree.length > 0) {
                                    lastNestedTextTree.push(result);
                                    result = current;
                                }
                            } else {
                                // 并列结构
                                result = [current, result];
                            }
                        }
                    }
                    return result;
                }
            } else if (this.childrenStructType === NodeChildrenStructType.Parallel) {
                // 并列结构
                return [...childrenTextTrees];
            }
        }
    }

    private removeDuplicates(arr: any[], ...otherArrays: any[][]): any[] {
        const duplicates = new Set(otherArrays.flat());
        return arr.filter(item => !duplicates.has(item));
    }

    private getSimpleTextTree(textTree: Array<any> | undefined | TextLeafNode): Array<any> | undefined | TextLeafNode {
        if (Array.isArray(textTree) && textTree.length === 1) {
            return this.getSimpleTextTree(textTree[0]);
        }
        return textTree;
    }

    private getLastNestedTextTree(textTree: Array<any>
        | undefined
        | TextLeafNode): Array<any> | undefined | TextLeafNode {
        if (Array.isArray(textTree) && textTree.length === 2 && Array.isArray(textTree[1])) {
            return this.getLastNestedTextTree(textTree[1]);
        }
        return textTree;
    }
}
